import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { BroadcastService } from '../../core/services/broadcast.service';
import { CachedCollection } from '../common/cached-collection';
import { ImageAsset } from '../images/image-asset-model';
import { Member } from './member.model';
import { MemberApiService } from './member-api.service';
import { MemberBasicEditModel, MemberFullEditModel } from './member-edit.model';
import { MemberCountWithCategoriesResult } from './member-count-with-categories-result.model';
import { MyCampApp } from '../common/enumerations';
import { ServiceManagerBase } from '../common/service-manager-base';
import { TextMessage } from './text-message.model';
import { RecentUploadProgress, MemberUploadResult, CategoryUploadResult } from './member-upload-result.model';
import { Organization } from '../organization/organization.model';
import { MembershipType } from '../organization/membership-type-item.model';
import { MemberNeedingSmsResponse } from './member-needing-sms-response.model';
import { ChildrenApiService } from '../children/children-api.service';
import { ChildrenManagerService } from '../children/children-manager.service';


@Injectable({
    providedIn: 'root'
})
export class MemberManagerService extends ServiceManagerBase
{
    // Cache Containers
    private _membersCollection?: CachedCollection<Member> = undefined;


    constructor(private memberApiService: MemberApiService,
                private childManager: ChildrenManagerService,
                protected override broadcastService: BroadcastService)
    {
        super(broadcastService);
    }

    clearCache()
    {
        if (this.retrievingData) return;
        this._membersCollection = undefined;
    }

    getMembers(sortField?: string, sortOrder?: MyCampApp.SortOrder, pageIndex = 0, pageSize = 100000, bypassCache = false): Observable<Member[]>
    {
        if (!bypassCache && this._membersCollection && !this._membersCollection.isExpired)
        {
            return of(this._membersCollection.collection);
        }

        this.retrievingData = true;
        return this.memberApiService.getMembers(sortField, sortOrder, pageIndex, pageSize).pipe(tap(response =>
        {
            this._membersCollection = new CachedCollection(response);
            this.retrievingData = false;
        }));
    }

    getMember(memberId: number): Observable<Member>
    {
        return this.memberApiService.getMember(memberId);
    }

    searchMembers(searchString: string): Observable<Member[]>
    {
        return this.memberApiService.searchMembers(searchString);
    }

    saveMember(memberEditModel: MemberFullEditModel): Observable<Member>
    {
        this.childManager.clearCache();

        if (memberEditModel.Id === 0)
        {
            return this.memberApiService.createMember(memberEditModel)
                .pipe(tap(createdMember =>
                    {
                        if (this._membersCollection === undefined)
                            this._membersCollection = new CachedCollection([]);
                        memberEditModel.Id = createdMember.Id;
                        this._membersCollection.addItem(this.updateListMemberWithFullMember(createdMember));
                    })
                );
        }

        return this.memberApiService.updateMember(memberEditModel)
            .pipe(map(updatedMember =>
                {
                    const item: Member = this._membersCollection!.collection.find(member => member.Id === memberEditModel.Id)!;
                    Object.assign(item, this.updateListMemberWithFullMember(updatedMember));
                    return item;
                })
            );
    }

    deleteMember(member: Member, includeChildren: boolean): Observable<any>
    {
        return this.memberApiService.deleteMember(member, includeChildren)
            .pipe(tap(() =>
                {
                    this.childManager.clearCache();
                    if (this._membersCollection === undefined) return;
                    this._membersCollection.removeItem(this._membersCollection!.collection.find(m => m.Id === member.Id)!);
                })
            );
    }

    deleteMembers(memberIds: number[], includeChildren: boolean): Observable<any>
    {
        return this.memberApiService.deleteMembers(memberIds, includeChildren)
            .pipe(tap(() =>
                {
                    this.childManager.clearCache();
                    if (this._membersCollection === undefined) return;
                    memberIds.forEach(id => this._membersCollection!.removeItem(this._membersCollection!.collection.find(m => m.Id === id)!));
                })
            );
    }

    addCategoriesForMembers(memberIds: number[], categoryIds: number[], organization: Organization): Observable<any>
    {
        return this.memberApiService.addCategoriesForMembers(memberIds, categoryIds).pipe(tap(() =>
            {
                if (this._membersCollection === undefined) return;
                memberIds.forEach(memberId =>
                {
                    const member = this._membersCollection!.collection.find(m => m.Id === memberId)!;
                    const legalCategoryIdsForMember = organization.getCategoriesForMembershipType(member.MembershipTypeId).map(mt => mt.Id);
                    categoryIds.filter(categoryId => legalCategoryIdsForMember.includes(categoryId) && !member.CategoryIds.includes(categoryId))
                        .forEach(categoryId => member.CategoryIds.push(categoryId));
                });
            })
        );
    }

    deleteCategoriesForMembers(memberIds: number[], categoryIds: number[]): Observable<any>
    {
        return this.memberApiService.deleteCategoriesForMembers(memberIds, categoryIds).pipe(tap(() =>
            {
                if (this._membersCollection === undefined) return;
                memberIds.forEach(memberId =>
                {
                    const member = this._membersCollection!.collection.find(m => m.Id === memberId)!;
                    member.CategoryIds = member.CategoryIds.filter(existingCategoryId => !categoryIds.includes(existingCategoryId));
                });
            })
        );
    }

    setMembershipTypeForMembers(memberIds: number[], membershipTypeId: number): Observable<any>
    {
        return this.memberApiService.setMembershipTypeForMembers(memberIds, membershipTypeId).pipe(tap(() =>
            {
                if (this._membersCollection === undefined) return;
                memberIds.forEach(memberId =>
                {
                    const member = this._membersCollection!.collection.find(m => m.Id === memberId)!;
                    // member.CategoryIds = member.CategoryIds.filter(existingCategoryId => !categoryIds.includes(existingCategoryId));
                });
            })
        );
    }

    setCategoriesForMembers(memberIds: number[], categoryIds: number[]): Observable<any>
    {
        return this.memberApiService.setCategoriesForMembers(memberIds, categoryIds).pipe(tap(() =>
            {
                if (this._membersCollection === undefined) return;
                memberIds.forEach(id =>
                {
                    const member = this._membersCollection!.collection.find(m => m.Id === id)!;
                    member.CategoryIds = categoryIds;
                });
            })
        );
    }

    setImageForMember(memberEditModel: MemberBasicEditModel, previousPhoto?: ImageAsset, uploadedImageUrl?: string): Observable<ImageAsset | undefined>
    {
        // See if the image is being removed
        if (previousPhoto != null && memberEditModel.PhotoId === -1)
        {
            return this.memberApiService.deleteImageForMember(memberEditModel.Id)
                .pipe(
                    tap( () =>
                    {
                        if (this._membersCollection != null)
                        {
                            const item = this._membersCollection!.collection.find(member => member.Id === memberEditModel.Id)!;
                            item.Photo = undefined;
                        }
                    })
                );
        }

        // Possibly adding image
        if (uploadedImageUrl == null) return of(undefined);
        return this.memberApiService.setImageForMember(memberEditModel.Id, uploadedImageUrl)
            .pipe(
                tap( (photo: ImageAsset) =>
                {
                    if (this._membersCollection == undefined) return;
                    const item = this._membersCollection!.collection.find(member => member.Id === memberEditModel.Id)!;
                    item.Photo = photo;
                })
            );
    }

    getRecentUploadProgress(): Observable<RecentUploadProgress>
    {
        return this.memberApiService.getRecentUploadProgress();
    }

    uploadMemberList(file: File, overwriteExistingMembers: boolean, continueOnUploadError: boolean, uploadUserTypeId: number): Observable<MemberUploadResult>
    {
        return this.memberApiService.uploadMemberList(file, overwriteExistingMembers, continueOnUploadError, uploadUserTypeId);
    }

    uploadCategoryList(file: File, overwriteExistingCategories: boolean, deleteExistingCategories: boolean): Observable<CategoryUploadResult>
    {
        return this.memberApiService.uploadCategoryList(file, overwriteExistingCategories, deleteExistingCategories);
    }

    downloadMemberList(memberIds: number[]): Observable<any>
    {
        return this.memberApiService.downloadMemberList(memberIds);
    }

    syncCampDoc(): Observable<Response>
    {
        return this.memberApiService.syncCampDoc();
    }

    getMemberCountForCategories(categoryIds: number[]): Observable<MemberCountWithCategoriesResult>
    {
        return this.memberApiService.getMemberCountForCategories(categoryIds);
    }

    getConversation(memberId: number): Observable<TextMessage[]>
    {
        return this.memberApiService.getConversation(memberId);
    }

    sendSmsToMember(memberId: number, message: string): Observable<TextMessage>
    {
        return this.memberApiService.sendSmsToMember(memberId, message).pipe(map(json => new TextMessage(json)));
    }

    acknowledgeSmsFromMember(memberId: number): Observable<any>
    {
        return this.memberApiService.acknowledgeSmsFromMember(memberId);
    }

    getMembersNeedingSmsResponse(): Observable<MemberNeedingSmsResponse[]>
    {
        return this.memberApiService.getMembersNeedingSmsResponse();
    }

    private updateListMemberWithFullMember(member: Member): Member
    {
        const listMember = <Member>{...member};
        listMember.MembershipTypeId = listMember.MemberInfo?.MembershipTypeId;
        listMember.CategoryIds = listMember.MemberInfo?.CategoryItems.map(ci => ci.Id) ?? [];
        return listMember;
    }
}
